/**
 * CleoChat — the shared chat panel used by both the floating drawer
 * and the /cleo full-page route.
 *
 * Streaming: uses `fetch` with a body reader. We parse SSE frames manually
 * (each frame is `data: {...}\n\n`).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Send, Trash2, Sparkles, X, Copy, Check, LifeBuoy } from 'lucide-react';
import { useTheme } from '../lib/theme';
import { API_BASE, getCleoThread, clearCleoThread } from '../lib/api';

const SUGGESTIONS = [
  { icon: '📊', label: 'Why is my score low?' },
  { icon: '📅', label: 'When are my next tax deadlines?' },
  { icon: '💸', label: 'How am I doing this month?' },
  { icon: '✅', label: 'What should I do before filing?' },
];

export default function CleoChat({ compact = false, onClose = null }) {
  const { colors } = useTheme();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [toolChip, setToolChip] = useState(null);
  const [copied, setCopied] = useState(false);
  const bottomRef = useRef(null);
  const abortRef = useRef(null);
  const streamingMsgIdRef = useRef(null);

  // Load prior thread on mount.
  useEffect(() => {
    (async () => {
      try {
        const doc = await getCleoThread();
        if (doc.messages?.length) setMessages(doc.messages);
      } catch {
        /* silent — empty state is fine */
      }
    })();
  }, []);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(scrollToBottom, [messages, streaming, toolChip, scrollToBottom]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || streaming) return;
    const now = new Date().toISOString();
    const userMsg = { id: `u-${Date.now()}`, role: 'user', content: text.trim(), created_at: now };
    const asstId = `a-${Date.now()}`;
    streamingMsgIdRef.current = asstId;
    setMessages((prev) => [...prev, userMsg, { id: asstId, role: 'assistant', content: '', created_at: now, streaming: true }]);
    setInput('');
    setStreaming(true);
    setToolChip(null);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const res = await fetch(`${API_BASE}/api/cleo/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({ content: text.trim() }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        throw new Error(`chat_http_${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let done = false;
      while (!done) {
        const chunk = await reader.read();
        if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true });
        // Consume complete SSE frames terminated by "\n\n".
        for (;;) {
          const idx = buffer.indexOf('\n\n');
          if (idx === -1) break;
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = frame.split('\n').filter((l) => l.startsWith('data:'));
          for (const l of lines) {
            const jsonStr = l.slice(5).trim();
            if (!jsonStr) continue;
            let evt;
            try { evt = JSON.parse(jsonStr); } catch { continue; }
            handleEvent(evt, asstId);
          }
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      setMessages((prev) => prev.map((m) => m.id === asstId
        ? { ...m, content: (m.content || '') + `\n\n_Something went wrong — please try again._`, streaming: false }
        : m,
      ));
    } finally {
      setStreaming(false);
      setToolChip(null);
      abortRef.current = null;
      streamingMsgIdRef.current = null;
    }
  }, [streaming]);

  function handleEvent(evt, asstId) {
    if (evt.type === 'delta') {
      setMessages((prev) => prev.map((m) => m.id === asstId
        ? { ...m, content: (m.content || '') + evt.text }
        : m,
      ));
    } else if (evt.type === 'tool_call') {
      if (evt.status === 'start') {
        setToolChip({ name: evt.name, friendly: evt.friendly });
      } else if (evt.status === 'complete') {
        setToolChip(null);
      }
    } else if (evt.type === 'done') {
      setMessages((prev) => prev.map((m) => m.id === asstId
        ? { ...m, streaming: false }
        : m,
      ));
    } else if (evt.type === 'error') {
      setMessages((prev) => prev.map((m) => m.id === asstId
        ? { ...m, content: (m.content || '') + `\n\n_Error: ${evt.message}_`, streaming: false }
        : m,
      ));
    }
  }

  const handleClear = useCallback(async () => {
    if (streaming) return;
    if (!window.confirm('Clear this conversation with Cleo?')) return;
    try { await clearCleoThread(); } catch { /* silent */ }
    setMessages([]);
  }, [streaming]);

  const supportEmail = 'support@clasifi.ca';
  const handleCopyEmail = useCallback(() => {
    navigator.clipboard?.writeText(supportEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, []);

  const isEmpty = messages.length === 0;

  return (
    <div
      data-testid="cleo-chat"
      className="flex flex-col h-full"
      style={{ backgroundColor: colors.bg }}>
      {/* Header */}
      <div
        className="flex items-center gap-3 px-5 py-3 border-b"
        style={{ borderColor: colors.border, backgroundColor: colors.surface }}>
        <img
          src="/cleo-avatar.png"
          alt="Cleo"
          className="w-9 h-9 rounded-full"
          style={{ boxShadow: `0 0 12px ${colors.accent}55` }}
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold" style={{ color: colors.text }}>Cleo</p>
          <p className="text-xs" style={{ color: colors.textMuted }}>Your Clasifi assistant</p>
        </div>
        <button
          data-testid="cleo-clear-btn"
          onClick={handleClear}
          disabled={streaming || isEmpty}
          title="Clear chat"
          className="p-2 rounded-lg transition-opacity hover:opacity-80 disabled:opacity-30"
          style={{ color: colors.textMuted }}>
          <Trash2 size={16} />
        </button>
        {onClose ? (
          <button
            data-testid="cleo-close-btn"
            onClick={onClose}
            className="p-2 rounded-lg transition-opacity hover:opacity-80"
            style={{ color: colors.textMuted }}>
            <X size={18} />
          </button>
        ) : null}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="cleo-messages">
        {isEmpty ? (
          <EmptyState
            colors={colors}
            onPick={(t) => sendMessage(t)}
            onCopyEmail={handleCopyEmail}
            copied={copied} />
        ) : (
          messages.map((m) => <Bubble key={m.id} m={m} colors={colors} />)
        )}
        {toolChip ? (
          <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-full self-start w-fit"
            data-testid="cleo-tool-chip"
            style={{
              backgroundColor: `${colors.accent}18`, color: colors.accent,
              border: `1px solid ${colors.accent}44`,
            }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ backgroundColor: colors.accent }} />
            {toolChip.friendly}
          </div>
        ) : null}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t px-3 py-3" style={{ borderColor: colors.border, backgroundColor: colors.surface }}>
        <div
          className="flex items-end gap-2 rounded-2xl p-2"
          style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}>
          <textarea
            data-testid="cleo-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage(input);
              }
            }}
            placeholder={streaming ? 'Cleo is thinking…' : 'Ask Cleo anything about your books…'}
            rows={compact ? 1 : 2}
            disabled={streaming}
            className="flex-1 resize-none bg-transparent px-3 py-2 text-sm outline-none disabled:opacity-60"
            style={{ color: colors.text, minHeight: 40, maxHeight: 140 }}
          />
          <button
            data-testid="cleo-send-btn"
            onClick={() => sendMessage(input)}
            disabled={streaming || !input.trim()}
            className="p-2.5 rounded-xl text-white transition-opacity disabled:opacity-40"
            style={{ backgroundColor: colors.accent }}
            title="Send (Enter)">
            <Send size={16} />
          </button>
        </div>
        <p className="text-[10px] mt-2 px-2" style={{ color: colors.textMuted }}>
          Cleo is a helpful guide — always double-check important tax decisions with a licensed accountant.
        </p>
      </div>
    </div>
  );
}


function Bubble({ m, colors }) {
  const isUser = m.role === 'user';
  return (
    <div className={`flex items-end gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}
      data-testid={`cleo-bubble-${m.role}`}>
      {!isUser ? (
        <img src="/cleo-avatar.png" alt="Cleo"
          className="w-6 h-6 rounded-full flex-shrink-0"
          style={{ boxShadow: `0 0 8px ${colors.accent}44` }} />
      ) : null}
      <div className="max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap break-words"
        style={{
          backgroundColor: isUser
            ? colors.surface
            : `${colors.accent}12`,
          color: colors.text,
          border: `1px solid ${isUser ? colors.border : colors.accent + '33'}`,
          backdropFilter: 'blur(8px)',
        }}>
        {m.content || (m.streaming ? '…' : '')}
        {m.streaming ? (
          <span className="inline-block w-1 h-3 ml-0.5 align-middle animate-pulse"
            style={{ backgroundColor: colors.accent }} />
        ) : null}
      </div>
    </div>
  );
}


function EmptyState({ colors, onPick, onCopyEmail, copied }) {
  return (
    <div className="flex flex-col items-center text-center px-4 py-6" data-testid="cleo-empty">
      <img src="/cleo-avatar.png" alt="Cleo"
        className="w-14 h-14 rounded-full mb-3"
        style={{ boxShadow: `0 0 20px ${colors.accent}66` }} />
      <p className="text-lg font-bold" style={{ color: colors.text }}>{"Hi, I'm Cleo 👋"}</p>
      <p className="text-xs mt-1 max-w-xs" style={{ color: colors.textMuted }}>
        Ask me about your Clasifi Score, spending, Canadian tax deadlines, or what to do before filing.
      </p>

      <div className="grid grid-cols-1 gap-2 mt-4 w-full max-w-sm">
        {SUGGESTIONS.map((s) => (
          <button
            key={s.label}
            data-testid={`cleo-suggest-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
            onClick={() => onPick(s.label)}
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-left transition-opacity hover:opacity-80"
            style={{
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
              color: colors.text,
            }}>
            <span>{s.icon}</span>
            <span className="flex-1">{s.label}</span>
            <Sparkles size={12} style={{ color: colors.accent }} />
          </button>
        ))}
      </div>

      <div className="mt-5 pt-4 border-t w-full max-w-sm" style={{ borderColor: colors.border }}>
        <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: colors.textMuted }}>Human support</p>
        <div className="flex flex-col gap-2">
          <button
            data-testid="cleo-copy-support-email"
            onClick={onCopyEmail}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
            style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}`, color: colors.text }}>
            {copied ? <Check size={12} style={{ color: colors.accent }} /> : <Copy size={12} />}
            <span>support@clasifi.ca</span>
            <span className="ml-auto" style={{ color: colors.textMuted }}>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
          <a
            data-testid="cleo-support-link"
            href="/app/support"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
            style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}`, color: colors.text }}>
            <LifeBuoy size={12} />
            <span>Open Clasifi Support form</span>
          </a>
        </div>
      </div>
    </div>
  );
}
